
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import LoadingAnimation from "../components/LoadingAnimation";
import { Helmet } from "react-helmet-async";
export default function ProductDetailPage() {
const { productType = "consumer", productSlug } = useParams();


  // URL format: /product/adult-diaper-pants-size-l-34
  const productId = productSlug?.match(/-(\d+)$/)?.[1];
  const canonicalPath =
  productType === "consumer"
    ? `/product/${productSlug}`
    : `/product/${productType}/${productSlug}`;

const canonicalUrl =
  `https://pharmahelp.co.in${canonicalPath}`;

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function fetchProduct() {
      if (!productId) {
        setError("Invalid product URL.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      setProduct(null);

      try {
        let endpoint;

if (productType === "generic" || productType === "branded") {
  endpoint = `/public/medicines/${productId}/`;
} else if (productType === "surgical") {
  endpoint = `/public/surgical/${productId}/`;
} else if (productType === "consumer") {
  endpoint = `/consumer/products/${productId}/`;
} else {
  throw new Error("Unsupported product type");
}

const response = await axiosInstance.get(endpoint);
        if (!cancelled) {
          setProduct(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.status === 404
              ? "Product not found."
              : "Unable to load this product. Please try again."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchProduct();

    return () => {
      cancelled = true;
    };
 }, [productId, productType]);


if (loading) {
  return <LoadingAnimation />;
}

if (error || !product) {
  return (
    <main style={styles.container}>
      <h2>{error || "Product not found."}</h2>
      <Link to="/public-customer">
        Back to PharmaHelp
      </Link>
    </main>
  );
}

  

  const name = product.name || "Product";

  const sellingPrice = Number(
    product.selling_price ?? product.price ?? 0
  );
  const mrp = Number(product.mrp ?? 0);
  const stock = Number(product.stock ?? 0);

  
const imageList = [
  ...(product.image ? [product.image] : []),
  ...(Array.isArray(product.images) ? product.images : []),
].filter(
  (url, index, list) =>
    typeof url === "string" &&
    url.startsWith("https://") &&
    list.indexOf(url) === index
);

const image = imageList[0] || "";


  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: name,
    ...(imageList.length ? { image: imageList } : {}),
    description: `${name} — check product details, price and availability on PharmaHelp.`,
    ...(product.company
  ? {
      brand: {
        "@type": "Brand",
        name: product.company,
      },
    }
  : {}),
    offers: {
      "@type": "Offer",
      url: canonicalUrl,
hasMerchantReturnPolicy: {
  "@type": "MerchantReturnPolicy",
  applicableCountry: "IN",
  returnPolicyCategory:
    "https://schema.org/MerchantReturnNotPermitted",
  returnPolicyUrl:
    "https://pharmahelp.co.in/refund-cancellation-policy",
},
      priceCurrency: "INR",
      price: sellingPrice.toFixed(2),
      availability:
        stock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };


return (
  <>
    <Helmet>
      <title>{`${name} | PharmaHelp`}</title>

<script type="application/ld+json">
  {JSON.stringify(productSchema)}
</script>

      <meta
        name="description"
        content={`${name} — check product details, price and availability on PharmaHelp.`}
      />

      <link rel="canonical" href={canonicalUrl} />

<meta
  property="og:url"
  content={canonicalUrl}
/>

      <meta property="og:type" content="product" />
      <meta property="og:title" content={`${name} | PharmaHelp`} />
      <meta
        property="og:description"
        content={`${name} — check product details and availability on PharmaHelp.`}
      />

      {image && <meta property="og:image" content={image} />}
      
    </Helmet>

    <main style={styles.container}>
      <Link to="/public-customer" style={styles.backLink}>
        ← Back to PharmaHelp
      </Link>

      <section style={styles.card}>
        {image ? (
          <img
            src={image}
            alt={name}
            style={styles.image}
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div style={styles.noImage}>No image available</div>
        )}

        <div style={styles.details}>
          <h1 style={styles.title}>{name}</h1>

          {product.category && (
            <p style={styles.category}>
              Category: {product.category}
            </p>
          )}

          {mrp > sellingPrice && (
            <p style={styles.mrp}>MRP: ₹{mrp.toFixed(2)}</p>
          )}

          <p style={styles.price}>
            ₹{sellingPrice.toFixed(2)}
          </p>

          <p style={stock > 0 ? styles.inStock : styles.outOfStock}>
            {stock > 0 ? "In stock" : "Currently unavailable"}
          </p>

          <p style={styles.note}>
            Please check the product label and applicable usage
            instructions before purchase.
          </p>

          <Link to="/login" state={{ from: window.location.pathname }}
            style={styles.button}
          >
            Login to order
          </Link>
        </div>
      </section>
    </main>
</>
  );
}

const styles = {
  container: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: 20,
    paddingTop: "calc(20px + env(safe-area-inset-top))",
  },
  backLink: {
    display: "inline-block",
    marginBottom: 20,
    color: "#15803d",
    textDecoration: "none",
    fontWeight: 600,
  },
  card: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
    gap: 28,
    alignItems: "start",
    padding: 20,
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    background: "#fff",
  },
  image: {
    width: "100%",
    maxHeight: 380,
    objectFit: "contain",
  },
  noImage: {
    minHeight: 200,
    display: "grid",
    placeItems: "center",
    background: "#f8fafc",
    color: "#64748b",
  },
  details: {
    minWidth: 0,
  },
  title: {
    fontSize: 26,
    color: "#1f2937",
    overflowWrap: "anywhere",
  },
  category: {
    color: "#64748b",
  },
  mrp: {
    color: "#dc2626",
    textDecoration: "line-through",
  },
  price: {
    fontSize: 26,
    fontWeight: 700,
    color: "#15803d",
  },
  inStock: {
    color: "#15803d",
    fontWeight: 600,
  },
  outOfStock: {
    color: "#dc2626",
    fontWeight: 600,
  },
  note: {
    color: "#64748b",
    lineHeight: 1.6,
  },
  button: {
    display: "inline-block",
    padding: "12px 18px",
    borderRadius: 8,
    background: "#15803d",
    color: "#fff",
    textDecoration: "none",
    fontWeight: 700,
  },
};
